import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { ArrowUpRight, Menu, X } from 'lucide-react';
import { MagneticButton } from '@/components/site/MagneticButton';
import { SITE, SERVICES } from '@/data/site';

function DeltaMark({ className = '' }) {
  return (
    <svg viewBox="0 0 32 32" className={className} aria-hidden>
      <defs>
        <linearGradient id="delta-g" x1="0" x2="1" y1="0" y2="1">
          <stop offset="0%" stopColor="#6C5CE7" />
          <stop offset="100%" stopColor="#4B9FFF" />
        </linearGradient>
      </defs>
      <path d="M16 4 L29 27 L3 27 Z" fill="none" stroke="url(#delta-g)" strokeWidth="2" strokeLinejoin="round" />
      <path d="M16 11 L23 24 L9 24 Z" fill="url(#delta-g)" opacity="0.85" />
    </svg>
  );
}

const NAV = [
  { to: '/', label: 'Home' },
  { to: '/services/', label: 'Services', mega: true },
  { to: '/work/', label: 'Work' },
  { to: '/about/', label: 'About' },
  { to: '/website-development-cost/', label: 'Pricing' },
  { to: '/contact/', label: 'Contact' },
];

export function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setMegaOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,border,backdrop-filter] duration-500 ${
        scrolled
          ? 'bg-[#0A0A0F]/75 backdrop-blur-xl border-b border-white/10'
          : 'bg-transparent border-b border-white/0'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
        <Link
          to="/"
          data-testid="header-logo"
          className="flex items-center gap-2.5 group"
          aria-label="NioDelta home"
        >
          <DeltaMark className="h-7 w-7 transition-transform duration-500 group-hover:rotate-[20deg]" />
          <span className="text-[15px] font-semibold tracking-tight">
            Nio<span className="text-ink-muted">Delta</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1" data-testid="header-nav">
          {NAV.map((item) =>
            item.mega ? (
              <div
                key={item.to}
                className="relative"
                onMouseEnter={() => setMegaOpen(true)}
                onMouseLeave={() => setMegaOpen(false)}
              >
                <NavLink
                  to={item.to}
                  data-testid={`nav-link-${item.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `relative px-4 py-2 text-sm transition-colors ${
                      isActive ? 'text-white' : 'text-ink-muted hover:text-white'
                    }`
                  }
                >
                  {item.label}
                  <span className="ml-1.5 inline-block translate-y-[-1px] text-[10px] opacity-60">▾</span>
                </NavLink>
                {megaOpen && (
                  <div
                    data-testid="services-mega-menu"
                    className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[min(880px,90vw)]"
                  >
                    <div className="rounded-[16px] border border-white/10 bg-[#0A0A0F]/95 backdrop-blur-xl p-6 shadow-[0_20px_80px_-20px_rgba(0,0,0,0.7)]">
                      <div className="text-[10px] uppercase tracking-[0.28em] text-ink-muted mb-5 px-2">
                        — Six services, one studio
                      </div>
                      <div className="grid grid-cols-2 gap-1">
                        {SERVICES.map((s) => (
                          <Link
                            key={s.slug}
                            to={`/services/${s.slug}/`}
                            data-testid={`mega-service-${s.slug}`}
                            className="arrow-link group/item rounded-xl px-3 py-3 hover:bg-white/[0.04] transition-colors border border-transparent hover:border-white/10"
                          >
                            <div className="flex items-baseline justify-between">
                              <div className="flex items-baseline gap-3">
                                <span className="text-[11px] tabular-nums text-ink-muted">{s.num}</span>
                                <span className="text-[15px] font-medium tracking-tight text-white">
                                  {s.title}
                                </span>
                              </div>
                              <ArrowUpRight
                                size={14}
                                className="arrow text-ink-muted group-hover/item:text-white"
                              />
                            </div>
                            <p className="mt-1 pl-8 text-[13px] text-ink-muted leading-snug">
                              {s.short}
                            </p>
                          </Link>
                        ))}
                      </div>
                      <div className="mt-5 pt-5 border-t border-white/10 px-2 flex items-center justify-between">
                        <span className="text-xs text-ink-muted">Not sure where you fit?</span>
                        <Link
                          to="/contact/"
                          className="text-xs text-white arrow-link inline-flex items-center gap-2"
                        >
                          Tell us the problem <span className="arrow">→</span>
                        </Link>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-link-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm transition-colors ${
                    isActive ? 'text-white' : 'text-ink-muted hover:text-white'
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:block">
          <MagneticButton
            href={SITE.whatsapp}
            external
            variant="primary"
            data-testid="header-cta-whatsapp"
          >
            Book discovery call
            <ArrowUpRight size={15} />
          </MagneticButton>
        </div>

        <button
          type="button"
          className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full border border-white/10 text-white"
          onClick={() => setMobileOpen((s) => !s)}
          aria-label="Toggle menu"
          data-testid="header-mobile-toggle"
        >
          {mobileOpen ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden border-t border-white/10 transition-[max-height] duration-500 bg-[#0A0A0F]/95 backdrop-blur-xl ${
          mobileOpen ? 'max-h-[640px]' : 'max-h-0'
        }`}
      >
        <div className="px-6 py-6 space-y-1" data-testid="mobile-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`mobile-nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `block py-3 text-2xl tracking-tight font-medium border-b border-white/5 ${
                  isActive ? 'text-white' : 'text-ink-muted'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          <div className="pt-5">
            <MagneticButton
              href={SITE.whatsapp}
              external
              variant="primary"
              data-testid="mobile-cta-whatsapp"
            >
              Book discovery call
              <ArrowUpRight size={15} />
            </MagneticButton>
          </div>
        </div>
      </div>
    </header>
  );
}
